import { useMediaQuery, useTheme } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';

import { Consent } from '../../api/consent';
import { getSigningStatus, SigningStatus } from '../../api/getSigningStatus';
import { getSigningLinkService } from '../../api/signApplication';
import ConsentField from '../../components/ConsentField/ConsentField';
import { ErrorMessage } from '../../components/ErrorMessage';
import PagesFooter from '../../components/PagesFooter/PagesFooter';
import PagesHeader from '../../components/PagesHeader/PagesHeader';
import Spinner from '../../components/Spinner/Spinner';
import Title from '../../components/Title/Title';
import { usePagesContext } from '../../context/PagesContext';
import { useAppSelector } from '../../hooks';
import useArnFromPath from '../../hooks/useArnFromPath';
import useComponentTracker from '../../hooks/useComponentTracker';
import useDefaultPagesAction from '../../hooks/useDefaultPagesAction';
import useGetConsentService from '../../hooks/useGetConsentService';
import { usePageFocus } from '../../hooks/usePageFocusHook';
import usePagesConfig from '../../hooks/usePagesConfig';
import useSetBannerType from '../../hooks/useSetBannerType';
import useSetRightPanelContent from '../../hooks/useSetRightPanelContent';
import useSettings from '../../hooks/useSettings';
import useTranslation from '../../hooks/useTranslation';
import { selectConsentFieldStates } from '../../store/applicationform/applicationFormSelectors';
import { IFieldState } from '../../store/applicationform/applicationFormTypes';
import OngoingStaticPage from '../StatusPage/OngoingStaticPage';
import SignAgreementPageSidePanel from './SignAgreementPageSidePanel';

const SignAgreementPage = () => {
    usePageFocus();
    const translate = useTranslation();
    const theme = useTheme();
    const greaterThanLg = useMediaQuery(theme.breakpoints.up('lg'));
    const { currentPage } = usePagesContext();
    const defaultPagesAction = useDefaultPagesAction();
    const { totalSections } = usePagesConfig();
    const { authenticationData } = useAppSelector((state) => state.authentication);
    const { apiKey, proxyBaseUrl } = useSettings();
    const [errorOccurred, setErrorOccurred] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [signingStatus, setSigningStatus] = useState<SigningStatus>();
    const applicationReferenceNumber = useArnFromPath();
    const {
        consentDetails,
        isLoading: isConsentLoading,
        isError: isConsentError,
    } = useGetConsentService('MARKETING_ELECTRONIC');
    const consentsStates = useAppSelector(selectConsentFieldStates)();
    useComponentTracker('SignAgreementPage');
    const isSigningComplete = signingStatus?.signingStatus === 'COMPLETE' && signingStatus.waitingFor === 'NONE';

    const showForm = !isLoading && signingStatus?.signingStatus !== 'COMPLETE';

    useSetBannerType('none');

    const sidePanelContent = useMemo(() => <SignAgreementPageSidePanel />, []);
    useSetRightPanelContent(greaterThanLg && showForm ? sidePanelContent : null);

    useEffect(() => {
        setIsLoading(true);
        getSigningStatus(proxyBaseUrl, apiKey, applicationReferenceNumber, authenticationData)
            .then((response) => setSigningStatus(response))
            .finally(() => setIsLoading(false));
    }, []);

    useEffect(() => {
        setErrorOccurred(false);
    }, []);

    const convertToConsentProposals = (consentFieldStates: IFieldState[], consentDetails: Consent[] | undefined) => {
        // Match on the full CONSENT:{consentType}:{id} name, not just the trailing id: ids are not
        // guaranteed unique across consent types, so matching by id alone could resolve a stale field left
        // over from a different consent-type's page to this page's viewId and send it as that consent's
        // answer instead of dropping it.
        return consentFieldStates
            .map((field) => ({
                field,
                consent: consentDetails?.find((consent) => consent.name === field.fieldName),
            }))
            .filter(({ consent }) => consent !== undefined)
            .map(({ field, consent }) => ({
                viewId: consent!.viewId,
                id: consent!.id,
                status: String(field.value) === 'true' ? 'OBTAINED' : 'NONE',
            }));
    };

    const onAction = async () => {
        const consentProposals = convertToConsentProposals(consentsStates, consentDetails);

        try {
            const response = await getSigningLinkService(
                proxyBaseUrl,
                apiKey,
                applicationReferenceNumber,
                consentProposals,
                true,
                authenticationData,
            );
            window.location.assign(response.signLink);
        } catch (error) {
            setErrorOccurred(true);
            throw error;
        }

        return true;
    };

    return isLoading ? (
        <Spinner size="xl" />
    ) : signingStatus?.signingStatus === 'COMPLETE' && signingStatus.waitingFor === 'NONE' ? (
        <OngoingStaticPage applicationState="TRANSITION" />
    ) : signingStatus?.signingStatus === 'COMPLETE' && signingStatus.waitingFor === 'CO' ? (
        <OngoingStaticPage applicationState="WAITING_FOR_CO_APPLICANT" />
    ) : signingStatus?.signingStatus === 'COMPLETE' && signingStatus.waitingFor === 'MAIN' ? (
        <OngoingStaticPage applicationState="WAITING_FOR_MAIN_APPLICANT" />
    ) : (
        <>
            <PagesHeader
                actions={[]}
                onAction={defaultPagesAction}
                currentPage={currentPage}
                currentSection={5}
                numberOfSections={totalSections}
                numberOfPagesInSection={1}
                currentPageInSection={1}
                sectionTitle={translate('field.section.finalize')}
            />
            <Title text={translate('page.sign-agreement.title')} />

            {!greaterThanLg && <SignAgreementPageSidePanel />}
            {consentDetails?.map((consentDetail, index) => (
                <ConsentField key={index} consentDetail={consentDetail} />
            ))}
            {errorOccurred && (
                <ErrorMessage
                    title={translate(`page.sign-agreement.error.title`)}
                    description={translate(`page.submit.error.description`)}
                />
            )}
            {/*
             * A failed consent fetch leaves consentDetails undefined but isLoading false, so without this,
             * signing would silently proceed with an empty consentProposals -- treating a consent-service
             * outage as a successful "no consent" response. Block signing and surface the error instead.
             */}
            {isConsentError && (
                <ErrorMessage title={translate('page.error.title')} description={translate('page.error.description')} />
            )}
            <PagesFooter
                actions={isSigningComplete || isConsentLoading || isConsentError ? [] : ['sign']}
                onAction={onAction}
                currentSection={5}
                numberOfSections={totalSections}
                numberOfPagesInSection={1}
                currentPageInSection={1}
                sectionTitle={translate('field.section.finalize')}
            />
        </>
    );
};

export default SignAgreementPage;
